class BzSidebar extends HTMLElement {
  constructor() {
    super();
    this._handleOutsideClick = this._handleOutsideClick.bind(this);
    this._handleKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (this.activeMega && this.contains(document.activeElement)) {
        this.closeMega(true);
        return;
      }
      if (this.classList.contains('open')) this.close();
      else if (this.activeMega) this.closeMega();
    };
    this._handleResize = this._handleResize.bind(this);
    this._handleToggleClick = () => this.toggle();
    this._handleToggleKey = (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      this.toggle();
    };
    // Keep the page still while the drawer is open (theme editor scrolls to selected blocks)
    this._onLockScroll = () => this._lockY != null && Math.abs(scrollY - this._lockY) > 1 && scrollTo(0, this._lockY);
  }

  connectedCallback() {
    this.headerType = this.getAttribute('data-header-type') || 'wide';
    this.isMegaDrawer = this.classList.contains('bz_mega_drawer');
    this.toggleBtn = document.getElementById(this.getAttribute('data-toggle-btn'));
    this.toggleBtn?.addEventListener('click', this._handleToggleClick);
    this.toggleBtn?.addEventListener('keydown', this._handleToggleKey);
    document.addEventListener('click', this._handleOutsideClick);
    document.addEventListener('keydown', this._handleKeyDown);
    window.addEventListener('resize', this._handleResize);
    this._handleResize();
    if (this.isMegaDrawer) {
      this._initMegaDrawer();
      this._initDrawerKeyboard();
    }
  }

  // Drawer: the mega panel sits after the whole menu list in the DOM. Tab from the panel's last
  // control goes to the menu item after the open one; Shift+Tab from its first control goes back
  // to that item (instead of wrapping to the top of the menu).
  _initDrawerKeyboard() {
    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab' || !this.activeMega) return;
      const A = window.BzA11y;
      if (!A) return;
      const closeBtn = this.querySelector('[data-sidebar-close]');
      const left = this.querySelector('.bz_drawer_left');
      // Last item of the menu list: the open mega was already visited from its own item,
      // so Tab goes on to the close button
      if (!e.shiftKey && left?.contains(document.activeElement) && closeBtn && A.isVisible(closeBtn)) {
        const leftItems = A.getFocusable(left);
        if (document.activeElement === leftItems[leftItems.length - 1]) {
          e.preventDefault();
          closeBtn.focus();
          return;
        }
      }
      if (!this.panel?.contains(document.activeElement)) return;
      const items = A.getFocusable(this.panel);
      const trigger = this.querySelector('.bz_drawer_left [data-mega-trigger].is-active, [data-mega-trigger].is-active');
      const control = trigger && this._controlOf(trigger);
      if (!items.length || !control) return;
      const leftHidden = !A.isVisible(control); // mobile: the open mega covers the list
      if (e.shiftKey && document.activeElement === items[0]) {
        e.preventDefault();
        if (leftHidden) this.closeMega(true);
        else control.focus();
      } else if (!e.shiftKey && document.activeElement === items[items.length - 1]) {
        e.preventDefault();
        if (leftHidden) this.closeMega();
        const list = A.getFocusable(left || this);
        const next = list[list.indexOf(control) + 1];
        (next || closeBtn || control).focus();
      }
    });
  }

  disconnectedCallback() {
    this.toggleBtn?.removeEventListener('click', this._handleToggleClick);
    this.toggleBtn?.removeEventListener('keydown', this._handleToggleKey);
    document.removeEventListener('click', this._handleOutsideClick);
    document.removeEventListener('keydown', this._handleKeyDown);
    window.removeEventListener('resize', this._handleResize);
    document.removeEventListener('shopify:block:select', this._onBlockSelect);
    this._megaObserver?.disconnect();
    [this._closeTimer, this._switchTimer, this._hideTimer].forEach(clearTimeout);
    this._lockScroll(false);
    document.body.classList.remove('overflow-hidden', 'sidebar_open');
  }

  _isActive() {
    return this.classList.contains('sidebar_menu');
  }

  _setOpenState(isOpen) {
    this.classList.toggle('open', isOpen);
    this.toggleBtn?.classList.toggle('toggle_active', isOpen);
    this.toggleBtn?.setAttribute('aria-expanded', String(isOpen));
    document.body.classList.toggle('overflow-hidden', isOpen);
    document.body.classList.toggle('sidebar_open', isOpen);
  }

  _lockScroll(on) {
    if (on) {
      if (this._lockY == null) addEventListener('scroll', this._onLockScroll, { passive: true });
      this._lockY = scrollY;
      // Header must stay visible while the drawer is open
      this.closest('.header_wrap')?.classList.remove('scroll_up');
      this.closest('.header_sticky')?.classList.remove('scroll_up');
    } else if (this._lockY != null) {
      removeEventListener('scroll', this._onLockScroll);
      this._lockY = null;
    }
  }

  open() {
    if (!this._isActive()) return;
    if (this.isMegaDrawer) {
      this._lockScroll(true);
      this._setDrawerTop();
      this._syncMegaWidth();
    }
    this._setOpenState(true);
    this._saveState();
    window.BzA11y?.trapFocus(this, {
      returnTo: this.toggleBtn,
      initial: window.Shopify?.designMode ? false : undefined,
    });
  }

  close(immediate) {
    // Mega open: slide the mega back first, then close the left part
    if (!immediate && this.classList.contains('mega_open')) {
      this.closeMega();
      clearTimeout(this._closeTimer);
      this._closeTimer = setTimeout(() => this.close(true), 550);
      return;
    }
    clearTimeout(this._closeTimer);
    const wasOpen = this.classList.contains('open');
    this._setOpenState(false);
    this._lockScroll(false);
    if (this.isMegaDrawer) this.closeMega();
    this._saveState();
    if (wasOpen) window.BzA11y?.releaseFocus(this, { returnTo: this.toggleBtn });
  }

  toggle() {
    if (this._isActive()) this.classList.contains('open') ? this.close() : this.open();
  }

  _handleOutsideClick(e) {
    if (!this.classList.contains('open') || this.toggleBtn?.contains(e.target)) return;
    // Mega drawer: click on the backdrop closes it
    const onBackdrop = this.isMegaDrawer && (e.target === this || e.target.hasAttribute('data-drawer-backdrop'));
    if (onBackdrop || !this.contains(e.target)) this.close();
  }

  _handleResize() {
    if (this.headerType === 'wide') {
      const isMobile = innerWidth <= 991;
      this.classList.toggle('sidebar_menu', isMobile);
      if (!isMobile) this.close(true);
      if (this.toggleBtn) this.toggleBtn.style.display = isMobile ? '' : 'none';
      this.getAttribute('data-color-schemes')?.split(' ').forEach((cls) => cls && this.classList.toggle(cls, isMobile));
    } else if (this.headerType === 'sidebar') {
      this.classList.add('sidebar_menu');
      if (this.toggleBtn) this.toggleBtn.style.display = '';
      if (this.isMegaDrawer && this.classList.contains('open')) this._setDrawerTop();
      this._syncMegaWidth();
    }
  }

  /* ---------- Mega drawer (left menu + right mega panel) ---------- */
  _initMegaDrawer() {
    this.panel = this.querySelector('[data-mega-panel]');
    this.backLabel = this.querySelector('[data-mega-back-label]');
    this.megaBox = this.querySelector('.bz_mega_items') || this.panel;
    this.activeMega = null;
    this._bindMegaItems();
    this._syncMegaWidth();

    // One click handler for the whole menu (capture: runs before bz-dropdown)
    this.querySelector('.full_menu')?.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-mega-trigger]');
      if (!trigger) {
        // A normal menu item closes the open mega
        if (this.activeMega && !e.target.closest('[data-mega-bound]') && e.target.closest('.menu_wrap > li')) this.closeMega();
        return;
      }
      e.stopImmediatePropagation();
      const link = e.target.closest('a[href]');
      if (link && link !== trigger && trigger.contains(link)) return; // only the label text is a link
      e.preventDefault();
      const key = trigger.getAttribute('data-mega-trigger');
      const fromKeyboard = e.detail === 0;
      trigger.closest('bz-dropdown')?.removeAttribute('open');
      if (this.activeMega === key && (fromKeyboard || (innerWidth >= 992 && this.clickToggles !== false))) {
        this.closeMega(fromKeyboard);
        return;
      }
      this.openMega(key);
      if (fromKeyboard && this.activeMega === key) this._focusMega();
    }, true);

    this.querySelectorAll('[data-mega-back]').forEach((btn) => btn.addEventListener('click', () => this.closeMega(true)));
    this.querySelectorAll('[data-sidebar-close]').forEach((btn) => btn.addEventListener('click', () => this.close()));

    if (!window.Shopify?.designMode) return;

    // Theme editor: open the drawer + matching mega when a block is selected
    this._onBlockSelect = (event) => {
      const item = this.contains(event.target) && event.target.closest?.('[data-mega-title]');
      if (!item) return;
      this._bindMegaItems();
      this.open();
      this.openMega(item.getAttribute('data-mega-title'));
      setTimeout(() => this._closeMenuDropdowns(), 30);
    };
    document.addEventListener('shopify:block:select', this._onBlockSelect);

    // Block settings re-render only the block: re-bind menu items when "Menu title" changes
    if (this.panel) {
      this._megaObserver = new MutationObserver(() => {
        clearTimeout(this._rebindTimer);
        this._rebindTimer = setTimeout(() => this._bindMegaItems(), 60);
      });
      this._megaObserver.observe(this.panel, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-mega-title'] });
    }

    const state = BzSidebar.designState;
    if (state?.open) {
      this.open();
      if (state.mega) this.openMega(state.mega);
    }
  }

  _saveState() {
    if (this.isMegaDrawer) BzSidebar.designState = { open: this.classList.contains('open'), mega: this.activeMega };
  }

  // Menu links show once per menu title. Liquid already does this; this also covers the theme
  // editor, where a block re-renders on its own.
  // - Same title on more than one "Menu with banner": only the first shows the links.
  // - Links at the top of other mega blocks: only the first block shows them, and none when a
  //   Menu with banner already shows them.
  _dedupeMenus() {
    const seen = new Set();
    this.panel?.querySelectorAll('.bz_mega_item:not(.bz_mega_item--default) > .bz_mwb:not(.bz_mwb--links)').forEach((mwb) => {
      const key = (mwb.parentElement.getAttribute('data-mega-title') || '').trim();
      const menus = mwb.querySelector(':scope > .bz_mwb_menus');
      if (!mwb.dataset.cols) mwb.dataset.cols = (mwb.className.match(/bz_mwb--cols-(\d)/) || [])[1] || '0';
      const dup = !!(key && menus && seen.has(key));
      if (key && menus && !dup) seen.add(key);
      if (menus) menus.hidden = dup;
      mwb.classList.remove('bz_mwb--cols-0', 'bz_mwb--cols-1', 'bz_mwb--cols-2', 'bz_mwb--cols-3');
      mwb.classList.add(`bz_mwb--cols-${dup ? 0 : mwb.dataset.cols}`);
    });
    this.panel?.querySelectorAll('.bz_mega_item > .bz_mwb--links').forEach((links) => {
      const key = (links.parentElement.getAttribute('data-mega-title') || '').trim();
      links.hidden = !key || seen.has(key);
      if (key) seen.add(key);
    });
  }

  // Mark each menu item that has a mega block (by title) as a mega trigger
  _bindMegaItems() {
    this._dedupeMenus();
    this.panel?.querySelectorAll(this.bindSelector || '[data-mega-title]').forEach((item) => {
      const key = item.getAttribute('data-mega-title').trim();
      if (!key) return;
      this.querySelectorAll(`.menu_wrap > li[data-menu-handle="${CSS.escape(key)}"]:not([data-mega-bound])`).forEach((li) => {
        li.setAttribute('data-mega-bound', key);
        if (li.querySelector(':scope > [data-mega-trigger]')) return;
        const dropdown = li.querySelector(':scope > bz-dropdown');
        dropdown?.classList.add('bz-mega-bound');
        const trigger = dropdown ? dropdown.querySelector(':scope > bz-button') : this._linkToTrigger(li.querySelector(':scope > a'));
        trigger?.querySelector('.bz_mlabel')?.setAttribute('data-mega-label', '');
        trigger?.setAttribute('data-mega-trigger', key);
        trigger?.classList.add('bz_mega_trigger');
      });
    });
  }

  // Menu item without child links: the row opens the mega, the label text stays the link
  _linkToTrigger(link) {
    if (!link) return null;
    const row = document.createElement('div');
    row.className = link.className;

    const badges = [...link.querySelectorAll(':scope > .bz_menu_badge')];
    badges.forEach((badge) => badge.remove());
    link.className = 'break bz_mlabel';
    link.replaceWith(row);

    const wrap = document.createElement('span');
    wrap.className = 'bz_mlabel_wrap';
    wrap.append(link, ...badges);

    let caret = this.querySelector('button.click_link')?.cloneNode(true);
    if (!caret) {
      caret = document.createElement('button');
      caret.type = 'button';
      caret.className = 'click_link flex_shrink flex_center';
      const icon = this.querySelector('.icon-caret');
      if (icon) caret.append(icon.cloneNode(true));
    }
    caret.setAttribute('aria-expanded', 'false');
    caret.setAttribute('aria-label', link.textContent.trim());
    row.append(wrap, caret);
    return row;
  }

  // The keyboard control of a menu row: its caret <button> (the label itself stays a link)
  _controlOf(trigger) {
    return trigger.querySelector(':scope > button.click_link, :scope > .click_link button, button.click_link') || trigger;
  }

  // Keyboard opened a mega: move focus to its first control once it is visible
  _focusMega() {
    clearTimeout(this._focusTimer);
    this._focusTimer = setTimeout(() => {
      const items = this.panel ? [...this.panel.querySelectorAll('[data-mega-title].is-active')] : [];
      const target = window.BzA11y?.getFocusable(this.panel).find((el) => items.some((item) => item.contains(el)) || el.matches('[data-mega-back]'));
      target?.focus({ preventScroll: true });
    }, 220);
  }

  _closeMenuDropdowns(onlyMegaBound) {
    this.querySelectorAll(`.full_menu bz-dropdown${onlyMegaBound ? '.bz-mega-bound' : ''}[open]`).forEach((el) => el.removeAttribute('open'));
  }

  // Set the active menu item (null = none). Returns the active item's label text.
  _markTrigger(key) {
    let label = '';
    this.querySelectorAll('[data-mega-trigger]').forEach((trigger) => {
      const active = trigger.getAttribute('data-mega-trigger') === key;
      trigger.classList.toggle('is-active', active);
      const control = this._controlOf(trigger);
      control.setAttribute('aria-expanded', String(active));
      trigger.closest('bz-dropdown')?.classList.toggle('is-active', active);
      if (active && !label) label = (trigger.querySelector('[data-mega-label], .bz_mlabel') || trigger).textContent.trim();
    });
    return label;
  }

  // Show only these mega items (and stop any running fade)
  _showItems(items) {
    this.megaBox?.getAnimations().forEach((a) => a.cancel());
    this.panel?.querySelectorAll('[data-mega-title].is-active').forEach((el) => !items.includes(el) && el.classList.remove('is-active'));
    items.forEach((el) => el.classList.add('is-active'));
  }

  // The whole mega content moves as one: old content fades out, new content fades in
  // and rises from below.
  _fade(fadeIn) {
    const box = this.megaBox;
    if (!box || matchMedia('(prefers-reduced-motion: reduce)').matches) return this._pauseScroll(false);
    const anim = box.animate(
      fadeIn ? { opacity: [0, 1], transform: ['translateY(30px)', 'none'] } : { opacity: [1, 0] },
      { duration: fadeIn ? 420 : 160, easing: fadeIn ? 'cubic-bezier(0.22, 0.61, 0.36, 1)' : 'ease', fill: fadeIn ? 'none' : 'forwards' }
    );
    if (fadeIn) anim.finished.catch(() => {}).finally(() => this._pauseScroll(false));
  }

  // Hide the panel scrollbar while switching to content that does not need one
  _pauseScroll(on) {
    if (on) this.panel.style.overflowY = 'hidden';
    else this.panel?.style.removeProperty('overflow-y');
  }

  // Will the panel scroll with these items shown? (measured instantly, nothing is painted)
  _needsScroll(items, leaving) {
    leaving.forEach((el) => el.classList.remove('is-active'));
    items.forEach((el) => el.classList.add('is-active'));
    const needs = this.panel.scrollHeight > this.panel.clientHeight + 1;
    items.forEach((el) => el.classList.remove('is-active'));
    leaving.forEach((el) => el.classList.add('is-active'));
    return needs;
  }

  openMega(key) {
    const all = key && this.panel ? [...this.panel.querySelectorAll(`[data-mega-title="${CSS.escape(key)}"]`)] : [];
    const blocks = all.filter((el) => !el.classList.contains('bz_mega_item--default'));
    const main = blocks.filter((el) => !el.classList.contains('bz_mega_item--hsb'));
    const items = main.length ? blocks : all;
    if (!items.length) return;

    const isSwitch = this.activeMega && this.activeMega !== key && this.classList.contains('mega_open');
    [this._closeTimer, this._switchTimer, this._hideTimer].forEach(clearTimeout);
    this._closeMenuDropdowns();
    const label = this._markTrigger(key);
    if (this.backLabel && label) this.backLabel.textContent = label;
    this.activeMega = key;
    this._syncMegaWidth();
    this.classList.add('mega_open');
    this._saveState();

    const show = () => {
      this._showItems(items);
      this.panel.scrollTop = 0;
      if (isSwitch) this._fade(true);
    };
    const leaving = [...this.panel.querySelectorAll('[data-mega-title].is-active')].filter((el) => !items.includes(el));
    if (isSwitch && leaving.length) {
      // New content fits: the scrollbar goes away right now, not after the switch
      this._pauseScroll(!this._needsScroll(items, leaving));
      this._fade(false);
      this._switchTimer = setTimeout(show, 160);
    } else {
      show();
    }
  }

  closeMega(focusTrigger) {
    const key = this.activeMega;
    clearTimeout(this._switchTimer);
    this._pauseScroll(false);
    this.classList.remove('mega_open');
    this._closeMenuDropdowns(true);
    this._markTrigger(null);
    this.activeMega = null;
    if (focusTrigger && key) {
      const trigger = this.querySelector(`[data-mega-trigger="${CSS.escape(key)}"]`);
      if (trigger) this._controlOf(trigger).focus({ preventScroll: true });
    }
    // Hide the old content after the slide-out, unless another mega opened meanwhile
    clearTimeout(this._hideTimer);
    this._hideTimer = setTimeout(() => !this.activeMega && this._showItems([]), 480);
  }

  // Open width of the box = inner width minus the close button (CSS vars used by sec-header.css)
  _syncMegaWidth() {
    const inner = this.isMegaDrawer && this.querySelector('.bz_drawer_inner');
    if (!inner?.clientWidth) return;
    const css = getComputedStyle(inner);
    const gap = parseFloat(css.columnGap || css.gap) || 12;
    const pad = (parseFloat(css.paddingLeft) || 0) + (parseFloat(css.paddingRight) || 0);
    const closeBtn = this.querySelector('.bz_drawer_close');
    const openW = Math.max(Math.round(inner.clientWidth - pad - (closeBtn ? closeBtn.offsetWidth + gap : 60)), 0);
    const leftW = parseFloat(getComputedStyle(this).getPropertyValue('--drawer-left-w')) || 285;
    this.style.setProperty('--drawer-open-w', `${openW}px`);
    this.style.setProperty('--drawer-right-w', `${Math.max(openW - leftW, 0)}px`);
  }

  _setDrawerTop() {
    const top = this.closest('header')?.getBoundingClientRect().top || 0;
    this.style.setProperty('--drawer-top', `${Math.max(top, 0)}px`);
  }
}
customElements.define('bz-sidebar', BzSidebar);


/* ---------- Wide navigation (992px and up): horizontal menu + full width mega panel ----------
   Same mega logic as the drawer (BzSidebar). The mega content (.bz_mega_items) moves here on
   desktop and back into the drawer on mobile, so it is rendered only once. */
class BzMegaWide extends BzSidebar {
  connectedCallback() {
    const header = this.closest('header') || document;
    this.drawerPanel = header.querySelector('.bz_mega_drawer [data-mega-panel]');
    this.box = header.querySelector('.bz_mega_items');
    this.clickToggles = this.dataset.openMode !== 'on_hover';
    // Only mega blocks turn nav items into triggers (child links alone use the small dropdown when it is on)
    this.bindSelector = '[data-mega-title]:not(.bz_mega_item--default)';
    this.panel = this.querySelector('[data-mega-panel]');
    document.addEventListener('click', this._handleOutsideClick);
    document.addEventListener('keydown', this._handleKeyDown);
    window.addEventListener('resize', this._handleResize);
    this._handleResize();
    this._initMegaDrawer();
    this._handleResize();
    if (!this.clickToggles) this._initHover();
    this._initKeyboard();
    if (window.Shopify?.designMode && BzMegaWide.lastMega) this.openMega(BzMegaWide.lastMega);
  }

  // Keyboard: the full width panel sits after the whole nav in the DOM, so Tab is routed
  // panel -> next nav item, Shift+Tab from the panel -> its menu item, and focus leaving closes it.
  _initKeyboard() {
    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab' || !this.activeMega || !this.panel?.contains(document.activeElement)) return;
      const items = window.BzA11y?.getFocusable(this.panel) || [];
      const trigger = this.querySelector('[data-mega-trigger].is-active');
      const control = trigger && this._controlOf(trigger);
      if (!items.length || !control) return;
      if (e.shiftKey && document.activeElement === items[0]) {
        e.preventDefault();
        control.focus();
      } else if (!e.shiftKey && document.activeElement === items[items.length - 1]) {
        const nav = window.BzA11y.getFocusable(this.querySelector('.bz_wide_nav'));
        const next = nav[nav.indexOf(control) + 1];
        if (!next) return;
        e.preventDefault();
        this.closeMega();
        next.focus();
      }
    });
    this.addEventListener('focusout', (e) => {
      if (!this.activeMega || !e.relatedTarget || this.contains(e.relatedTarget)) return;
      this.closeMega();
    });
  }

  _initHover() {
    const later = (fn, ms) => {
      clearTimeout(this._hoverTimer);
      this._hoverTimer = setTimeout(fn, ms);
    };
    this.addEventListener('mouseover', (e) => {
      const trigger = e.target.closest?.('[data-mega-trigger]');
      if (trigger) {
        const key = trigger.getAttribute('data-mega-trigger');
        later(() => this.activeMega !== key && this.openMega(key), 80);
      } else if (e.target.closest?.('.bz_wide_nav .menu_wrap > li')) {
        later(() => this.closeMega(), 80); // a normal menu item
      } else {
        clearTimeout(this._hoverTimer);
      }
    });
    this.addEventListener('mouseleave', () => later(() => this.closeMega(), 250));
  }

  // Desktop: mega content lives in this panel. Mobile: back in the drawer.
  _handleResize() {
    const desktop = innerWidth >= 992;
    const target = desktop ? this.panel : this.drawerPanel;
    if (!desktop && this.activeMega) this.closeMega();
    if (this.box && target && this.box.parentElement !== target) {
      target.append(this.box);
      // Re-bind menu items to the mega blocks now in this panel (desktop) or in the drawer (mobile)
      const owner = desktop ? (this.backLabel !== undefined ? this : null) : target.closest('bz-sidebar');
      if (owner?.panel) {
        if (!desktop) owner.megaBox = this.box;
        owner._bindMegaItems();
      }
    }
    this.megaBox = desktop ? this.box : null;
  }

  _handleOutsideClick(e) {
    if (this.activeMega && !this.contains(e.target)) this.closeMega();
  }

  open() {}

  close() {
    if (this.activeMega) this.closeMega();
  }

  openMega(key) {
    if (innerWidth < 992) return;
    // Panel max height = space below the header
    const bottom = this.closest('header')?.getBoundingClientRect().bottom || 0;
    this.style.setProperty('--mega-wide-top', `${Math.max(bottom, 0)}px`);
    super.openMega(key);
  }

  _saveState() {
    BzMegaWide.lastMega = this.activeMega;
  }

  closeMega(focusTrigger) {
    super.closeMega(focusTrigger);
    BzMegaWide.lastMega = null;
  }
}
customElements.define('bz-mega-wide', BzMegaWide);